<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const isSubmitting = ref(false);
const features = ref({
  captain: false,
  companies: false,
});

const featureItems = computed(() => [
  {
    key: 'captain',
    title: t('GENERAL_SETTINGS.FORM.OPTIONAL_MODULES.CAPTAIN.TITLE'),
    description: t('GENERAL_SETTINGS.FORM.OPTIONAL_MODULES.CAPTAIN.DESCRIPTION'),
  },
  {
    key: 'companies',
    title: t('GENERAL_SETTINGS.FORM.OPTIONAL_MODULES.COMPANIES.TITLE'),
    description: t(
      'GENERAL_SETTINGS.FORM.OPTIONAL_MODULES.COMPANIES.DESCRIPTION'
    ),
  },
]);

watch(
  currentAccount,
  account => {
    const accountFeatures = account?.features || {};

    features.value = {
      captain:
        accountFeatures.captain_integration === true ||
        accountFeatures.captain_integration_v2 === true ||
        accountFeatures.captain_tasks === true,
      companies: accountFeatures.companies === true,
    };
  },
  { immediate: true, deep: true }
);

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount(
      {
        account_features: {
          captain_integration: features.value.captain,
          captain_integration_v2: features.value.captain,
          captain_tasks: features.value.captain,
          companies: features.value.companies,
        },
      },
      { silent: true }
    );
    useAlert(t('GENERAL_SETTINGS.FORM.OPTIONAL_MODULES.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.OPTIONAL_MODULES.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full outline outline-1 outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <h3 class="text-heading-2 text-n-slate-12">
        {{ t('GENERAL_SETTINGS.FORM.OPTIONAL_MODULES.TITLE') }}
      </h3>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.OPTIONAL_MODULES.NOTE') }}
      </p>
    </div>

    <div class="px-5 py-4">
      <form class="grid gap-4" @submit.prevent="handleSubmit">
        <div
          class="rounded-xl border border-n-weak bg-n-solid-1 divide-y divide-n-weak"
        >
          <div
            v-for="item in featureItems"
            :key="item.key"
            class="flex items-start justify-between gap-4 p-4"
          >
            <div class="space-y-1">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ item.title }}
              </p>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ item.description }}
              </p>
            </div>
            <Switch v-model="features[item.key]" />
          </div>
        </div>

        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('GENERAL_SETTINGS.FORM.OPTIONAL_MODULES.SAVE')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
